import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { useGeolocation } from '../hooks/useGeolocation';
import { fetchFeed, clearFeed, setScope as setStoreScope } from '../store/slices/postsSlice';
import PostCard from '../components/PostCard';
import NewsCard from '../components/NewsCard';
import CreatePost from '../components/CreatePost';
import './Home.css';

const SCOPE_OPTIONS = [
  { label: 'City', value: 'city' },
  { label: 'State/Province', value: 'state' },
  { label: 'Country', value: 'country' },
];

const Home: React.FC = () => {
  const dispatch = useAppDispatch();
  const { feed, feedItems, loading, error, total } = useAppSelector((state) => state.posts);
  const location = useGeolocation();
  const routerLocation = useLocation();
  const [scope, setScope] = useState('city');
  const [lastGroupScope, setLastGroupScope] = useState('country');

  const loadFeed = useCallback(() => {
    if (!location.loading) {
      dispatch(clearFeed());
      dispatch(fetchFeed({
        latitude: location.latitude,
        longitude: location.longitude,
        scope,
        page: 1,
        limit: 8,
      }));
    }
  }, [dispatch, location.latitude, location.longitude, location.loading, scope]);

  useEffect(() => {
    loadFeed();
  }, [loadFeed]);

  // Reload feed when navigating to Home (clicking Home link)
  useEffect(() => {
    loadFeed();
  }, [routerLocation.key]);

  const handleScopeChange = (newScope: string) => {
    if (newScope !== 'news') {
      setLastGroupScope(newScope);
    }
    setScope(newScope);
    dispatch(setStoreScope(newScope));
  };

  if (location.loading) {
    return (
      <div className="home">
        <header className="page-header">
          <h2>Home</h2>
        </header>
        <div className="loading-state">
          <p>Getting your location...</p>
          <small>Make sure location services are enabled in your browser.</small>
        </div>
      </div>
    );
  }

  return (
    <div className="home">
      {location.error && (
        <div className="location-banner">
          <span className="location-banner-icon">📍</span>
          <span className="location-banner-text">{location.error}</span>
          <button className="location-banner-retry" onClick={location.retry}>Retry</button>
        </div>
      )}
      <header className="page-header">
        <h2>Home</h2>
        <div className="radius-selector">
          {SCOPE_OPTIONS.map((option) => (
            <button
              key={option.value}
              className={`radius-btn ${(scope === 'news' ? option.value === 'country' : scope === option.value) ? 'active' : ''}`}
              onClick={() => handleScopeChange(option.value)}
            >
              {option.label}
            </button>
          ))}
          <button
            className={`radius-btn news-btn ${scope === 'news' ? 'active' : ''}`}
            onClick={() => handleScopeChange(scope === 'news' ? 'country' : 'news')}
          >
            News
          </button>
        </div>
      </header>

      <CreatePost latitude={location.latitude} longitude={location.longitude} onPostCreated={loadFeed} />

      <div className="feed">
        {loading && feed.length === 0 && (
          <div className="loading-state">Loading posts...</div>
        )}

        {error && (
          <div className="error-state">{error}</div>
        )}

        {!loading && feed.length === 0 && feedItems.length === 0 && (
          <div className="empty-state">
            <p>No posts in your area yet</p>
            <small>Be the first to post something!</small>
          </div>
        )}

        {!loading && feed.length === 0 && feedItems.length > 0 && (
          <div className="news-only-banner">
            <p>No posts nearby yet — here's what's happening in the news</p>
          </div>
        )}

        {feedItems.map((item, index) =>
          item.type === 'news' && item.news ? (
            <NewsCard key={`news-${index}`} news={item.news} />
          ) : item.post ? (
            <PostCard key={item.post.id} post={item.post} />
          ) : null
        )}

        {feed.length > 0 && feed.length < total && (
          <button
            className="load-more"
            onClick={() => dispatch(fetchFeed({
              latitude: location.latitude,
              longitude: location.longitude,
              scope,
              page: Math.floor(feed.length / 8) + 1,
              limit: 8,
            }))}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Load more'}
          </button>
        )}
      </div>
    </div>
  );
};

export default Home;
